"use client";

import * as signalR from '@microsoft/signalr';
import { API_URL } from '@/lib/api-config';

// Types for PDF processing events
export interface PdfChunkProgress {
    chunkNumber: number;
    totalChunks: number;
    startPage: number;
    endPage: number;
    fileName: string;
    fileSizeBytes: number;
    progressPercent: number;
    timestamp: string;
}

export interface PdfProcessingStarted {
    documentId: string;
    totalPages: number;
    estimatedChunks: number;
    timestamp: string;
}

export interface PdfProcessingCompleted {
    documentId: string;
    totalChunks: number;
    durationMs: number;
    timestamp: string;
}

export interface PdfProcessingFailed {
    documentId: string;
    errorMessage: string;
    timestamp: string;
}

export type PdfHubEvents = {
    Connected: (data: { message: string; timestamp: string }) => void;
    Subscribed: (data: { documentId: string; message: string }) => void;
    ProcessingStarted: (data: PdfProcessingStarted) => void;
    ChunkCreated: (data: PdfChunkProgress) => void;
    ProcessingCompleted: (data: PdfProcessingCompleted) => void;
    ProcessingFailed: (data: PdfProcessingFailed) => void;
};

let connection: signalR.HubConnection | null = null;
let connectionPromise: Promise<void> | null = null;
let isInitializing = false;

type PdfHubEventName = keyof PdfHubEvents;
type PdfHubEventHandlerMap = {
    [EventName in PdfHubEventName]: PdfHubEvents[EventName][];
};

const eventHandlers: PdfHubEventHandlerMap = {
    Connected: [],
    Subscribed: [],
    ProcessingStarted: [],
    ChunkCreated: [],
    ProcessingCompleted: [],
    ProcessingFailed: [],
};

function createNewConnection(): signalR.HubConnection {
    const hubUrl = `${API_URL}/hubs/pdf`;

    const newConnection = new signalR.HubConnectionBuilder()
        .withUrl(hubUrl, {
            skipNegotiation: false,
            transport: signalR.HttpTransportType.WebSockets
        })
        .withAutomaticReconnect([0, 2000, 5000, 10000, 15000, 30000])
        .configureLogging(signalR.LogLevel.Information)
        .build();

    setupEventListeners(newConnection);
    return newConnection;
}

function setupEventListeners(conn: signalR.HubConnection): void {
    // Clear existing listeners
    conn.off('Connected');
    conn.off('Subscribed');
    conn.off('ProcessingStarted');
    conn.off('ChunkCreated');
    conn.off('ProcessingCompleted');
    conn.off('ProcessingFailed');

    // Set up new listeners
    conn.on('Connected', (data: { message: string; timestamp: string }) =>
        eventHandlers['Connected']?.forEach(h => h(data))
    );

    conn.on('Subscribed', (data: { documentId: string; message: string }) =>
        eventHandlers['Subscribed']?.forEach(h => h(data))
    );

    conn.on('ProcessingStarted', (data: PdfProcessingStarted) =>
        eventHandlers['ProcessingStarted']?.forEach(h => h(data))
    );

    conn.on('ChunkCreated', (data: PdfChunkProgress) =>
        eventHandlers['ChunkCreated']?.forEach(h => h(data))
    );

    conn.on('ProcessingCompleted', (data: PdfProcessingCompleted) =>
        eventHandlers['ProcessingCompleted']?.forEach(h => h(data))
    );

    conn.on('ProcessingFailed', (data: PdfProcessingFailed) =>
        eventHandlers['ProcessingFailed']?.forEach(h => h(data))
    );

}

async function safelyStopConnection(): Promise<void> {
    if (connection) {
        try {
            if (
                connection.state !== signalR.HubConnectionState.Disconnected &&
                connection.state !== signalR.HubConnectionState.Disconnecting
            ) {
                await connection.stop();
            }
        } catch { }
    }
}

export const PdfHubConnection = {
    async initialize(): Promise<void> {
        if (isInitializing && connectionPromise) {
            return connectionPromise;
        }
        if (connection && connection.state === signalR.HubConnectionState.Connected) {
            return;
        }
        if (
            connection &&
            (connection.state === signalR.HubConnectionState.Connecting ||
                connection.state === signalR.HubConnectionState.Reconnecting ||
                connection.state === signalR.HubConnectionState.Disconnecting)
        ) {
            await safelyStopConnection();
            connection = null;
        }

        isInitializing = true;

        connectionPromise = (async () => {
            try {
                if (!connection || connection.state === signalR.HubConnectionState.Disconnected) {
                    connection = createNewConnection();
                }
                if (connection.state !== signalR.HubConnectionState.Connected) {
                    const startPromise = connection.start();
                    const timeoutPromise = new Promise<void>((_, reject) =>
                        setTimeout(() => reject(new Error('Connection start timed out')), 10000)
                    );
                    await Promise.race([startPromise, timeoutPromise]);
                }
            } finally {
                isInitializing = false;
                connectionPromise = null;
            }
        })();
        return connectionPromise;
    },

    on<EventName extends PdfHubEventName>(event: EventName, callback: PdfHubEvents[EventName]) {
        eventHandlers[event].push(callback);
        return () => {
            const index = eventHandlers[event].indexOf(callback);
            if (index !== -1) eventHandlers[event].splice(index, 1);
        };
    },

    off<EventName extends PdfHubEventName>(event: EventName, callback?: PdfHubEvents[EventName]) {
        if (callback) {
            const index = eventHandlers[event].indexOf(callback);
            if (index !== -1) eventHandlers[event].splice(index, 1);
        } else {
            eventHandlers[event].length = 0;
        }
    },

    async subscribeToDocument(documentId: string): Promise<void> {
        if (!connection || connection.state !== signalR.HubConnectionState.Connected) {
            await this.initialize();
        }
        if (!connection || connection.state !== signalR.HubConnectionState.Connected) {
            throw new Error('SignalR connection not established');
        }
        await connection.invoke('SubscribeToDocument', documentId);
    },

    async unsubscribeFromDocument(documentId: string): Promise<void> {
        if (!connection || connection.state !== signalR.HubConnectionState.Connected) {
            return; // Silently ignore if not connected
        }
        try {
            await connection.invoke('UnsubscribeFromDocument', documentId);
        } catch { }
    },

    getConnectionState(): string {
        if (!connection) return 'Disconnected';
        return signalR.HubConnectionState[connection.state];
    },

    async disconnect() {
        await safelyStopConnection();
    }
};
